<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { postReq } from '../../../../services/http';
	import { gen2CloudReq, gen2Request } from '../../../../services/gen_2_http_reqs';
	import { format } from '../../../../services/i18n';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import {
		clearNameSyncIfTargetEmpty,
		flagsToSyncSelection,
		isNameSyncTargetEmpty,
		resolveNameSyncTarget,
	} from 'src/helpers/device_name_sync_helpers';
	//Stores
	import { get } from 'svelte/store';
	import { devices } from 'src/stores/lists/devices';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, ButtonV2, Input, Text, toast } from '@Shelly/shelly-design-system';
	import SyncName from '../../ApplicationSettings/SyncName.svelte';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { onDestroy } from 'svelte';

	export let device: Device;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	const id = real_id(device.id);
	const componentKey = get_gen_2_component_channel_structure(device);
	const channelMethod = `${get_gen_2_component_from_category(device)}.SetConfig`;

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? readDeviceSettings(id);
	$: isSingleChannel = (device.channels_count ?? 1) <= 1;
	$: storeDevice = $devices[device.id] ?? device;
	$: synced = !!storeDevice.name_sync;
	$: syncTarget = resolveNameSyncTarget(storeDevice);
	$: syncSelection = flagsToSyncSelection(storeDevice);

	let appName = '';
	let sysName = '';
	let channelName = '';
	let appCache = '';
	let sysCache = '';
	let channelCache = '';
	let appLoading = false;
	let sysLoading = false;
	let channelLoading = false;
	let initialized = false;
	let emptySyncCleared = false;
	/** When the device has no sys/component name (null), skip empty-field validation. */
	let sysInitiallyEmpty = true;
	let channelInitiallyEmpty = true;
	let prevSyncSelection: 'off' | 'sys' | 'channel' | undefined;

	/**
	 * Persist the app interface name. Merges from the devices store so
	 * name_sync / name_sync_target are not wiped.
	 */
	const updateAppName = async (name: string) => {
		const current = get(devices)[device.id] ?? device;
		const updated: Device = { ...current, name };
		devices.update(updated);
		const int_res = await postReq('interface/device/save', {
			id: device.id,
			data: JSON.stringify(updated),
		});
		return int_res.isok;
	};

	/**
	 * On entering this setting: if sync points at a still-empty sys/component name,
	 * reset to "Do not sync" so the next visit does not keep an empty sync target.
	 */
	$: if (isSingleChannel && settings && !emptySyncCleared && initialized) {
		emptySyncCleared = true;
		if (storeDevice.name_sync && isNameSyncTargetEmpty(storeDevice, settings)) {
			void clearNameSyncIfTargetEmpty(storeDevice, settings).then(() => {
				prevSyncSelection = 'off';
			});
		}
	}

	onDestroy(() => {
		if (!isSingleChannel || !settings) return;
		const current = get(devices)[device.id] ?? device;
		if (current.name_sync && isNameSyncTargetEmpty(current, settings)) {
			void clearNameSyncIfTargetEmpty(current, settings);
		}
	});

	$: if (settings || storeDevice) {
		const rawSys = settings?.sys?.device?.name;
		const rawChannel =
			device.type === 'SBTR-001AEU'
				? settings?.[`blutrv:${device.id.split('_2')[1]}`]?.name
				: settings?.[componentKey]?.name;

		const nextSys = typeof rawSys === 'string' ? rawSys : '';
		const nextChannel = typeof rawChannel === 'string' ? rawChannel : '';
		const nextApp = storeDevice.name ?? '';

		if (!initialized) {
			sysInitiallyEmpty = rawSys == null || String(rawSys).trim() === '';
			channelInitiallyEmpty = rawChannel == null || String(rawChannel).trim() === '';
			sysName = nextSys;
			channelName = nextChannel;
			appName = nextApp;
			sysCache = sysName;
			channelCache = channelName;
			appCache = appName;
			prevSyncSelection = syncSelection;
			initialized = true;
		} else {
			if (appName === appCache && nextApp !== appCache) {
				appName = nextApp;
				appCache = nextApp;
			}
			if (sysName === sysCache && nextSys !== sysCache) {
				sysName = nextSys;
				sysCache = nextSys;
			}
			if (channelName === channelCache && nextChannel !== channelCache) {
				channelName = nextChannel;
				channelCache = nextChannel;
			}
		}
	}

	/**
	 * When returning to "Do not sync", keep the name from the option we left
	 * (system or component), not the older app-only value.
	 */
	$: if (initialized && prevSyncSelection !== undefined && syncSelection !== prevSyncSelection) {
		if (syncSelection === 'off') {
			let fromTarget = '';
			if (prevSyncSelection === 'sys') {
				fromTarget = sysName;
			} else if (prevSyncSelection === 'channel') {
				fromTarget = channelName;
			}

			let keepName = (storeDevice.name ?? '').trim();
			if (typeof fromTarget === 'string' && fromTarget.trim() !== '') {
				keepName = fromTarget.trim();
			}

			if (keepName !== '') {
				appName = keepName;
				appCache = keepName;
				if (keepName !== storeDevice.name) {
					void updateAppName(keepName);
				}
			}
		}
		prevSyncSelection = syncSelection;
	}

	/** Empty error only when the device already had a non-null name. */
	$: sysError = !sysInitiallyEmpty && sysName.trim() === '';
	$: channelError = !channelInitiallyEmpty && channelName.trim() === '';
	$: appError = appName.trim() === '';

	const saveAppName = async () => {
		appName = appName.trim();
		if (appCache === appName || appName === '') return;

		appLoading = true;
		const appOk = await updateAppName(appName);
		if (!appOk) {
			toast.open({ type: 'danger', title: $format('error_global') });
			appLoading = false;
			return;
		}
		appCache = appName;
		toast.open({
			type: 'success',
			title:
				$format('device_name_scc_toast', { values: { value: appName } }) ||
				`Device name changed to ${appName}`,
		});
		appLoading = false;
	};

	const saveSysName = async () => {
		sysName = sysName.trim();
		if (sysCache === sysName) return;
		if (sysName === '' && !sysInitiallyEmpty) return;

		sysLoading = true;
		const params = {
			id: device.channel,
			config: { device: { name: sysName || null } },
		};
		const res: SettingRequest = await gen2Request(device, 'Sys.SetConfig', params);

		if (!res.isok) {
			toast.open({ type: 'danger', title: $format('error_global') });
			sysLoading = false;
			return;
		}

		if (settings?.sys?.device) {
			settings.sys.device.name = sysName || null;
			devices_settings.update_devices_settings(id, settings);
		}

		if (synced && syncTarget === 'sys' && sysName !== '') {
			const appOk = await updateAppName(sysName);
			if (!appOk) {
				toast.open({ type: 'danger', title: $format('error_global') });
				sysLoading = false;
				return;
			}
			appName = sysName;
			appCache = sysName;
		}

		sysCache = sysName;
		if (sysName !== '') sysInitiallyEmpty = false;
		toast.open({
			type: 'success',
			title:
				$format('device_name_scc_toast', { values: { value: sysName } }) ||
				`Device name changed to ${sysName}`,
		});
		sysLoading = false;
	};

	const saveChannelName = async () => {
		channelName = channelName.trim();
		if (channelCache === channelName) return;
		if (channelName === '' && !channelInitiallyEmpty) return;

		channelLoading = true;
		const params = { id: device.channel, config: { name: channelName || null } };
		let res: SettingRequest;

		if (device.type === 'SBTR-001AEU') {
			const BluTRVid = getTrvComponentByDevice(device)?.id;
			if (!BluTRVid) {
				channelLoading = false;
				return;
			}
			res = await gen2CloudReq(
				id,
				device.type,
				'blutrv.setconfig',
				{
					id: BluTRVid,
					config: { name: channelName || null },
				},
				true,
			);
			if (res?.errors) {
				if (res?.isok) {
					toast.open({
						type: 'success',
						title: $format('command_is_sent_to_the_device', {
							default: 'The command is sent to the device.',
						}),
					});
				} else {
					onSettingsResponse(res, device);
				}
			}
		} else {
			res = await gen2Request(device, channelMethod, params);
		}

		if (!res?.isok) {
			toast.open({ type: 'danger', title: $format('error_global') });
			channelLoading = false;
			return;
		}

		if (device.type === 'SBTR-001AEU') {
			const blutrvKey = `blutrv:${device.id.split('_2')[1]}`;
			if (settings?.[blutrvKey]) {
				settings[blutrvKey].name = channelName || null;
			}
		} else if (settings?.[componentKey]) {
			settings[componentKey].name = channelName || null;
		}
		devices_settings.update_devices_settings(id, settings);

		const shouldUpdateApp = isSingleChannel
			? synced && syncTarget === 'channel' && channelName !== ''
			: !!synced && channelName !== '';

		if (shouldUpdateApp) {
			const appOk = await updateAppName(channelName);
			if (!appOk) {
				toast.open({ type: 'danger', title: $format('error_global') });
				channelLoading = false;
				return;
			}
			appName = channelName;
			appCache = channelName;
		}

		channelCache = channelName;
		if (channelName !== '') channelInitiallyEmpty = false;
		toast.open({
			type: 'success',
			title:
				$format('device_name_scc_toast', { values: { value: channelName } }) ||
				`Device name changed to ${channelName}`,
		});
		channelLoading = false;
	};
</script>

{#if isSingleChannel}
	<Text class="mb-m" styleType="body-small">
		{$format('device_names_g2_description', {
			default:
				'This device has a system name and a component (channel) name. Choose a sync option to control which device name stays in sync with the app. With “Do not sync”, Save only updates the app name.',
		})}
	</Text>

	<Text styleType="h2" class="mb-s">
		{$format('title_name_sync_options', { default: 'Name sync' })}
	</Text>
	<SyncName variant="radios" allowSysTarget={true} {device} {sysName} {channelName}>
		<div slot="panel-off">
			<Text styleType="h2" class="mb-s">
				{$format('label_device_app_name', { default: 'Device app name' })}
			</Text>
			<Text class="mb-s" styleType="body-small">
				{$format('device_app_name_hint', {
					default:
						'The name shown for this device in the Shelly app only. It is not written to the device.',
				})}
			</Text>
			<Input
				class="mb-s"
				icon="fas fa-microchip"
				size="m"
				label={$format('label_device_app_name', { default: 'Device app name' })}
				error={appError
					? $format('input_error_empty_name', { default: 'Name cannot be empty' })
					: ''}
				bind:value={appName}
				on:enter={saveAppName}
				id="input-device-app-name"
				aria-labelledby="input-device-app-name"
			/>
			<svelte:component
				this={ButtonComp}
				loading={appLoading}
				size="m"
				width="full"
				on:click={saveAppName}
				disabled={appError || appCache === appName}
				aria-label={$format('button_save_device_app_name', {
					default: 'Save device app name',
				})}
			>
				{$format('button_save')}
			</svelte:component>
		</div>

		<div slot="panel-sys">
			<Text styleType="h2" class="mb-s">
				{$format('label_system_device_name', { default: 'System name' })}
			</Text>
			<Text class="mb-s" styleType="body-small">
				{$format('system_device_name_hint', {
					default:
						'The main device name shown in the device embedded web header. Saving also updates the device app name.',
				})}
			</Text>
			<Input
				class="mb-s"
				icon="fas fa-microchip"
				size="m"
				label={$format('label_system_device_name', { default: 'System name' })}
				error={sysError
					? $format('input_error_empty_name', { default: 'Name cannot be empty' })
					: ''}
				bind:value={sysName}
				on:enter={saveSysName}
				id="input-device-sys-name"
				aria-labelledby="input-device-sys-name"
			/>
			<svelte:component
				this={ButtonComp}
				loading={sysLoading}
				size="m"
				width="full"
				on:click={saveSysName}
				disabled={sysError || sysCache === sysName}
				aria-label={$format('button_save_system_name', { default: 'Save system name' })}
			>
				{$format('button_save')}
			</svelte:component>
		</div>

		<div slot="panel-channel">
			<Text styleType="h2" class="mb-s">
				{$format('label_component_channel_name', { default: 'Component name' })}
			</Text>
			<Text class="mb-s" styleType="body-small">
				{$format('component_channel_name_hint', {
					default:
						'The output / channel name shown on the device embedded web. Saving also updates the device app name.',
				})}
			</Text>
			<Input
				class="mb-s"
				icon="fas fa-microchip"
				size="m"
				label={$format('label_component_channel_name', { default: 'Component name' })}
				error={channelError
					? $format('input_error_empty_name', { default: 'Name cannot be empty' })
					: ''}
				bind:value={channelName}
				on:enter={saveChannelName}
				id="input-device-channel-name"
				aria-labelledby="input-device-channel-name"
			/>
			<svelte:component
				this={ButtonComp}
				loading={channelLoading}
				size="m"
				width="full"
				on:click={saveChannelName}
				disabled={channelError || channelCache === channelName}
				aria-label={$format('button_save_component_name', {
					default: 'Save component name',
				})}
			>
				{$format('button_save')}
			</svelte:component>
		</div>
	</SyncName>
{:else}
	<Input
		class="mb-s"
		icon="fas fa-microchip"
		size="m"
		label={$format('sub_settings_title_device_name')}
		error={channelError
			? $format('input_error_empty_name', { default: 'Name cannot be empty' })
			: ''}
		bind:value={channelName}
		on:enter={saveChannelName}
		id="input-51wkv69"
		aria-labelledby="input-51wkv69"
	/>
	<SyncName
		nameType="device"
		{device}
		info={$format('channel_rename_description_g2', {
			default:
				'Keep the channel name in sync with the name given in the application if "Sync name" is enabled. When sync is enabled, changes on the device for this channel name also update the app.',
		})}
	/>
	<svelte:component
		this={ButtonComp}
		loading={channelLoading}
		size="m"
		class="mt-s"
		width="full"
		on:click={saveChannelName}
		disabled={channelError || channelCache === channelName}
		aria-label={$format('button_save')}
	>
		{$format('button_save')}
	</svelte:component>
{/if}
